import { useEffect, useState, type CSSProperties } from "react";
import { listAdminFlights, type FlightItem } from "../../lib/api";
import { supabase } from "../../lib/supabase";
import RecipeStudioTabs from "../../components/admin/RecipeStudioTabs";

interface FlightPour {
  position: number;
  name: string;
  distillery: string | null;
  age: string | null;
  proof: number | null;
  notes: string | null;
}

export default function AdminTastingMat() {
  const [flights, setFlights] = useState<FlightItem[]>([]);
  const [selectedFlightId, setSelectedFlightId] = useState<string>("custom");
  const [flightName, setFlightName] = useState("Sentinel Reserve Bourbon Flight");
  const [flightSub, setFlightSub] = useState("Curated Single Barrel & Wheated Expressions");
  const [pours, setPours] = useState<FlightPour[]>([
    { position: 1, name: "Buffalo Trace Single Barrel", distillery: "Buffalo Trace Distillery", age: "8 Yr", proof: 90, notes: "Caramel, vanilla, rich toasted oak, sweet honey." },
    { position: 2, name: "Weller Antique 107 NCF", distillery: "Buffalo Trace Distillery", age: "NAS", proof: 107, notes: "Spicy cinnamon, sweet apple, dark cherry, full wheated finish." },
    { position: 3, name: "Eagle Rare 10 Yr Cask Strength", distillery: "Buffalo Trace Distillery", age: "10 Yr", proof: 90, notes: "Complex aromas of cocoa, candied almonds, orange peel, dry oak." },
    { position: 4, name: "Blanton's Gold Edition", distillery: "Buffalo Trace Distillery", age: "NAS", proof: 103, notes: "Peaches, cream, dark chocolate, spicy clover honey, lingering finish." },
  ]);
  const [pourCount, setPourCount] = useState<3 | 4>(4);

  // Search pour catalog for adding/editing
  const [searchCatalogQuery, setSearchCatalogQuery] = useState("");
  const [catalogResults, setCatalogResults] = useState<any[]>([]);
  const [editingPosition, setEditingPosition] = useState<number | null>(null);

  useEffect(() => {
    async function loadFlights() {
      try {
        const res = await listAdminFlights();
        if (res.flights) {
          setFlights(res.flights);
        }
      } catch (err) {
        console.error("Failed to load flights:", err);
      }
    }
    loadFlights();
  }, []);

  const handleSelectFlight = (flightId: string) => {
    setSelectedFlightId(flightId);
    if (flightId === "custom") {
      setFlightName("Custom Speakeasy Tasting Flight");
      setFlightSub("Private Library Selection");
      return;
    }
    const found = flights.find(f => f.id === flightId);
    if (found) {
      setFlightName(found.name);
      setFlightSub(found.description || "Curated Sentinel Room Library Selection");
      if (found.pours && found.pours.length > 0) {
        const mappedPours: FlightPour[] = found.pours.map((p, idx) => ({
          position: idx + 1,
          name: p.bottle_name || p.pour_name,
          distillery: "Reserve Selection",
          age: null,
          proof: null,
          notes: p.note || "Complex aromatics, caramel, barrel char, refined finish.",
        }));
        setPours(mappedPours);
        setPourCount(mappedPours.length >= 4 ? 4 : 3);
      }
    }
  };

  const handleSearchCatalog = async (q: string) => {
    setSearchCatalogQuery(q);
    if (!q.trim() || q.length < 2) {
      setCatalogResults([]);
      return;
    }
    try {
      const { data, error } = await supabase
        .from("menu_items")
        .select("id, name, distillery, age, proof, description, flavor_tags")
        .ilike("name", `%${q}%`)
        .limit(8);

      if (error) throw error;
      setCatalogResults(data || []);
    } catch (err) {
      console.error("Failed to search catalog:", err);
    }
  };

  const handleSelectPourFromCatalog = (product: any, position: number) => {
    setPours(prev => {
      const copy = [...prev];
      const idx = copy.findIndex(p => p.position === position);
      const newPour: FlightPour = {
        position,
        name: product.name,
        distillery: product.distillery || "Reserve Selection",
        age: product.age_statement ? `${product.age_statement} Yr` : null,
        proof: product.proof || null,
        notes: product.tasting_notes || "Caramel, toasted oak, warming spice.",
      };
      if (idx >= 0) {
        copy[idx] = newPour;
      } else {
        copy.push(newPour);
      }
      return copy;
    });
    setEditingPosition(null);
    setSearchCatalogQuery("");
    setCatalogResults([]);
  };

  const handleUpdatePourText = (pos: number, field: keyof FlightPour, val: any) => {
    setPours(prev => prev.map(p => p.position === pos ? { ...p, [field]: val } : p));
  };

  const handlePrint = () => {
    window.print();
  };

  const activePours = pours.slice(0, pourCount);

  return (
    <div style={styles.container}>
      <div className="no-print">
        <RecipeStudioTabs activeTab="tastingMat" badgeCounts={{ tastingMat: flights.length }} />

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Whiskey Tasting Mat Creator</h1>
            <p style={styles.subtitle}>
              Design and print landscape Glencairn flight mats with gold speakeasy branding for table service.
            </p>
          </div>
          <div style={styles.actions}>
            <button type="button" onClick={handlePrint} style={styles.printBtn}>
              🖨️ Print Flight Mat (Landscape)
            </button>
          </div>
        </div>

        {/* Curation Controls */}
        <div style={styles.controlsCard}>
          <div style={styles.controlRow}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Select Curated Flight or Custom</label>
              <select
                value={selectedFlightId}
                onChange={(e) => handleSelectFlight(e.target.value)}
                style={styles.select}
              >
                <option value="custom">-- Custom Tasting Flight --</option>
                {flights.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.name} ({f.pours?.length || 0} Pours)
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Flight Title</label>
              <input
                type="text"
                value={flightName}
                onChange={(e) => setFlightName(e.target.value)}
                style={styles.input}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Subtitle / Occasion</label>
              <input
                type="text"
                value={flightSub}
                onChange={(e) => setFlightSub(e.target.value)}
                style={styles.input}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Coaster Count</label>
              <div style={styles.toggleGroup}>
                <button
                  type="button"
                  onClick={() => setPourCount(3)}
                  style={{ ...styles.toggleBtn, ...(pourCount === 3 ? styles.toggleBtnActive : {}) }}
                >
                  3 Pours
                </button>
                <button
                  type="button"
                  onClick={() => setPourCount(4)}
                  style={{ ...styles.toggleBtn, ...(pourCount === 4 ? styles.toggleBtnActive : {}) }}
                >
                  4 Pours
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── PRINTABLE TASTING MAT CANVAS ────────────────────────────────────── */}
      <div style={styles.printCanvas} className="tasting-mat-printable">
        <div style={styles.matBorder}>
          {/* Header Banner */}
          <div style={styles.matHeader}>
            <div style={styles.matLogo}>THE SENTINEL ROOM</div>
            <div style={styles.matGoldBar} />
            <div style={styles.matTitle}>{flightName.toUpperCase()}</div>
            <div style={styles.matSubtitle}>{flightSub}</div>
          </div>

          {/* Glencairn Coaster Landing Spots Row */}
          <div style={styles.poursRow}>
            {activePours.map((p) => (
              <div key={p.position} style={styles.pourSlot}>
                {/* Round Glass Target Coaster */}
                <div style={styles.glassCoaster}>
                  <div style={styles.innerCoasterRing}>
                    <span style={styles.coasterNumber}>{p.position}</span>
                    <span style={styles.coasterHint}>PLACE GLENCAIRN</span>
                  </div>
                </div>

                {/* Pour Description Dossier */}
                <div style={styles.pourDetails}>
                  <h3 style={styles.pourName}>{p.name}</h3>
                  <div style={styles.pourMeta}>
                    <span>{p.distillery}</span>
                    {p.age && <span> · {p.age}</span>}
                    {p.proof && <span> · {p.proof}° Proof</span>}
                  </div>
                  <p style={styles.pourNotes}>{p.notes}</p>
                </div>

                {/* Edit Pour in Screen Mode */}
                <div className="no-print" style={styles.editWrap}>
                  <button
                    type="button"
                    onClick={() => setEditingPosition(editingPosition === p.position ? null : p.position)}
                    style={styles.editBtn}
                  >
                    ✏️ Edit Pour #{p.position}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Footer Branding */}
          <div style={styles.matFooter}>
            <span>SENTINEL RESERVE WHISKEY LIBRARY · ROOM 33</span>
            <span>PLEASE SIP RESPONSIBLY</span>
            <span>KANSAS CITY, MISSOURI</span>
          </div>
        </div>
      </div>

      {/* Quick Editor Modal for Pours */}
      {editingPosition !== null && (
        <div className="no-print" style={styles.modalBackdrop}>
          <div style={styles.modalCard}>
            <h3 style={styles.modalTitle}>Edit Pour #{editingPosition}</h3>
            
            <div style={styles.searchSection}>
              <label style={styles.label}>Search Rare Catalog</label>
              <input
                type="text"
                value={searchCatalogQuery}
                onChange={(e) => handleSearchCatalog(e.target.value)}
                placeholder="Search bottle name (e.g. Stagg, Weller, Pappy)…"
                style={styles.searchInput}
              />
              {catalogResults.length > 0 && (
                <div style={styles.resultsList}>
                  {catalogResults.map((res) => (
                    <div
                      key={res.id}
                      onClick={() => handleSelectPourFromCatalog(res, editingPosition)}
                      style={styles.resultItem}
                    >
                      <strong>{res.name}</strong>
                      <span style={{ fontSize: 11, color: "var(--muted)" }}>
                        {res.distillery} · {res.proof ? `${res.proof} proof` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.editFields}>
              <div style={styles.formRow}>
                <label style={styles.label}>Bottle Name</label>
                <input
                  type="text"
                  value={pours.find(p => p.position === editingPosition)?.name || ""}
                  onChange={(e) => handleUpdatePourText(editingPosition, "name", e.target.value)}
                  style={styles.modalInput}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                <div>
                  <label style={styles.label}>Distillery</label>
                  <input
                    type="text"
                    value={pours.find(p => p.position === editingPosition)?.distillery || ""}
                    onChange={(e) => handleUpdatePourText(editingPosition, "distillery", e.target.value)}
                    style={styles.modalInput}
                  />
                </div>
                <div>
                  <label style={styles.label}>Proof</label>
                  <input
                    type="number"
                    value={pours.find(p => p.position === editingPosition)?.proof || ""}
                    onChange={(e) => handleUpdatePourText(editingPosition, "proof", Number(e.target.value))}
                    style={styles.modalInput}
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <label style={styles.label}>Tasting Notes</label>
                <textarea
                  value={pours.find(p => p.position === editingPosition)?.notes || ""}
                  onChange={(e) => handleUpdatePourText(editingPosition, "notes", e.target.value)}
                  style={styles.modalTextarea}
                  rows={3}
                />
              </div>
            </div>

            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => setEditingPosition(null)}
                style={styles.closeBtn}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Stylesheet */}
      <style>{`
        @media print {
          @page {
            size: letter landscape;
            margin: 0.25in;
          }
          body {
            background: #ffffff !important;
            color: #000000 !important;
          }
          .no-print {
            display: none !important;
          }
          .tasting-mat-printable {
            background: #ffffff !important;
            color: #111111 !important;
            box-shadow: none !important;
            border: 2px solid #222222 !important;
            padding: 18px !important;
            margin: 0 !important;
            width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    padding: "24px 20px",
    background: "var(--bg, #0C0907)",
    color: "var(--text, #F7F5F0)",
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    margin: "0 0 4px",
    fontSize: 22,
    fontWeight: 700,
    color: "var(--text)",
  },
  subtitle: {
    margin: 0,
    fontSize: 13,
    color: "var(--muted)",
  },
  actions: {
    display: "flex",
    gap: 10,
  },
  printBtn: {
    background: "var(--gold)",
    border: "none",
    color: "#0D0B08",
    fontWeight: 700,
    padding: "10px 18px",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(249, 175, 54, 0.25)",
  },
  controlsCard: {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: "16px 20px",
    marginBottom: 24,
  },
  controlRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 16,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    color: "var(--muted)",
    letterSpacing: 0.5,
  },
  select: {
    padding: "8px 12px",
    background: "#080605",
    border: "1px solid var(--border)",
    borderRadius: 8,
    color: "var(--text)",
    fontSize: 13,
    outline: "none",
  },
  input: {
    padding: "8px 12px",
    background: "#080605",
    border: "1px solid var(--border)",
    borderRadius: 8,
    color: "var(--text)",
    fontSize: 13,
    outline: "none",
  },
  toggleGroup: {
    display: "flex",
    gap: 6,
  },
  toggleBtn: {
    flex: 1,
    padding: "8px 12px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid var(--border)",
    borderRadius: 6,
    color: "var(--muted)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  toggleBtnActive: {
    background: "rgba(249, 175, 54, 0.15)",
    borderColor: "var(--gold)",
    color: "var(--gold)",
    fontWeight: 700,
  },
  printCanvas: {
    background: "#120E0A",
    border: "2px solid rgba(249, 175, 54, 0.4)",
    borderRadius: 16,
    padding: "24px",
    boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
  },
  matBorder: {
    border: "1px solid rgba(249, 175, 54, 0.3)",
    borderRadius: 12,
    padding: "24px 20px",
  },
  matHeader: {
    textAlign: "center",
    marginBottom: 28,
  },
  matLogo: {
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: 4,
    color: "var(--gold)",
    marginBottom: 4,
    fontFamily: "Georgia, serif",
  },
  matGoldBar: {
    width: 60,
    height: 2,
    background: "var(--gold)",
    margin: "0 auto 8px",
  },
  matTitle: {
    fontSize: 20,
    fontWeight: 800,
    letterSpacing: 1.5,
    color: "var(--text)",
    fontFamily: "Georgia, serif",
    marginBottom: 4,
  },
  matSubtitle: {
    fontSize: 12,
    color: "var(--muted)",
    fontStyle: "italic",
  },
  poursRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 20,
    marginBottom: 24,
  },
  pourSlot: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  glassCoaster: {
    width: 100,
    height: 100,
    borderRadius: "50%",
    border: "2px dashed var(--gold)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
    background: "rgba(249, 175, 54, 0.04)",
  },
  innerCoasterRing: {
    width: 84,
    height: 84,
    borderRadius: "50%",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  coasterNumber: {
    fontSize: 20,
    fontWeight: 800,
    color: "var(--gold)",
    fontFamily: "Georgia, serif",
  },
  coasterHint: {
    fontSize: 7,
    fontWeight: 800,
    letterSpacing: 0.5,
    color: "var(--muted)",
  },
  pourDetails: {
    width: "100%",
  },
  pourName: {
    margin: "0 0 4px",
    fontSize: 14,
    fontWeight: 700,
    color: "var(--text)",
    fontFamily: "Georgia, serif",
  },
  pourMeta: {
    fontSize: 11,
    color: "var(--gold)",
    marginBottom: 8,
    fontWeight: 600,
  },
  pourNotes: {
    margin: 0,
    fontSize: 11,
    color: "var(--muted)",
    lineHeight: 1.4,
    fontStyle: "italic",
  },
  editWrap: {
    marginTop: 10,
  },
  editBtn: {
    background: "none",
    border: "1px solid var(--border)",
    color: "var(--muted)",
    fontSize: 11,
    padding: "4px 8px",
    borderRadius: 6,
    cursor: "pointer",
  },
  matFooter: {
    display: "flex",
    justifyContent: "space-between",
    borderTop: "1px solid rgba(249, 175, 54, 0.2)",
    paddingTop: 12,
    fontSize: 9,
    color: "var(--muted)",
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  modalCard: {
    background: "#16120E",
    border: "1px solid var(--gold)",
    borderRadius: 12,
    padding: "24px",
    maxWidth: 460,
    width: "100%",
  },
  modalTitle: {
    margin: "0 0 16px",
    fontSize: 18,
    fontWeight: 700,
    color: "var(--text)",
  },
  searchSection: {
    marginBottom: 16,
  },
  searchInput: {
    width: "100%",
    padding: "8px 12px",
    background: "#080605",
    border: "1px solid var(--border)",
    borderRadius: 8,
    color: "var(--text)",
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
  },
  resultsList: {
    maxHeight: 150,
    overflowY: "auto",
    background: "#0C0907",
    border: "1px solid var(--border)",
    borderRadius: 6,
    marginTop: 6,
  },
  resultItem: {
    padding: "8px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    fontSize: 12,
  },
  editFields: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginBottom: 16,
  },
  formRow: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  modalInput: {
    width: "100%",
    padding: "8px 10px",
    background: "#080605",
    border: "1px solid var(--border)",
    borderRadius: 6,
    color: "var(--text)",
    fontSize: 13,
    boxSizing: "border-box",
  },
  modalTextarea: {
    width: "100%",
    padding: "8px 10px",
    background: "#080605",
    border: "1px solid var(--border)",
    borderRadius: 6,
    color: "var(--text)",
    fontSize: 13,
    boxSizing: "border-box",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
  },
  closeBtn: {
    background: "var(--gold)",
    border: "none",
    color: "#0D0B08",
    fontWeight: 700,
    padding: "8px 16px",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
  },
};
